// Services Service
// Fetches services from API and transforms to match component expectations

import { useQuery } from "@tanstack/react-query";
import { generateSlug } from "../utils/slugUtils";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://insightpanel.sembian.net";

/**
 * Transform API service data to match component expectations
 */
const transformService = (apiService) => {
  // Construct full image URL if image path is provided
  // API returns: "assets/images/service/1763721999.png"
  // Need to construct: "https://insightpanel.../assets/images/service/1763721999.png"
  let imageUrl = null;
  if (apiService.image) {
    // If image path already starts with http/https, use as is
    if (
      apiService.image.startsWith("http://") ||
      apiService.image.startsWith("https://")
    ) {
      imageUrl = apiService.image;
    } else {
      // Otherwise, prepend API base URL
      imageUrl = apiService.image.startsWith("/")
        ? `${API_BASE_URL}${apiService.image}`
        : `${API_BASE_URL}/${apiService.image}`;
    }
  }

  return {
    id: apiService.id,
    name: apiService.name,
    tagline: apiService.tagline || "",
    description: apiService.description || "",
    other_details_html: apiService.details || "",
    image: imageUrl,
    drag_order: apiService.drag_order ?? null,
    _original: apiService,
  };
};

/**
 * Get all services
 * @returns {Promise<Array>} Array of all services
 */
export const getAllServices = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/services`);
    const result = await response.json();

    if (result.success && result.data) {
      // Transform each service and sort by drag_order (ascending)
      // Items without drag_order will be placed at the end
      return result.data.map(transformService).sort((a, b) => {
        const orderA = a.drag_order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.drag_order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }

    console.error("API response error:", result);
    return [];
  } catch (error) {
    console.error("Error fetching services:", error);
    return [];
  }
};

/**
 * Get single service by slug
 * @param {string} slug - Service slug
 * @returns {Promise<Object|null>} Service object or null if not found
 */
export const getService = async (slug) => {
  try {
    // First, get all services
    const services = await getAllServices();

    // Find service by slug
    const service = services.find((item) => generateSlug(item.name) === slug);
    return service || null;
  } catch (error) {
    console.error("Error loading service:", error);
    return null;
  }
};

/**
 * Get all service links (helper for nav)
 * @returns {Promise<Array>} Array of service links
 */
export const getServiceLinks = async () => {
  try {
    const services = await getAllServices();
    return services.map((service) => ({
      label: service.name,
      link: `/services/${generateSlug(service.name)}`,
    }));
  } catch (error) {
    console.error("Error fetching service links:", error);
    return [];
  }
};

/**
 * React Query hook to get all services
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useServices = () => {
  return useQuery({
    queryKey: ["services"],
    queryFn: getAllServices,
    staleTime: 5 * 60 * 1000, // 5 minutes - services don't change frequently
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for 1 hour
  });
};

/**
 * React Query hook to get a single service by slug
 * @param {string} slug - Service slug
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useService = (slug) => {
  return useQuery({
    queryKey: ["service", slug],
    queryFn: () => getService(slug),
    enabled: !!slug,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * React Query hook to get all service links
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useServiceLinks = () => {
  return useQuery({
    queryKey: ["serviceLinks"],
    queryFn: getServiceLinks,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};
